<template>
  <div class="ocean-account-page">
    <div class="page-title">
      <div>
        <h2>巨量账号目录</h2>
        <p>组织、账号和报表查询使用状态</p>
      </div>
      <div class="title-actions">
        <el-tag v-if="summary.syncRunning" type="warning" size="small">同步中</el-tag>
        <el-button size="small" icon="el-icon-refresh" :loading="syncLoading" @click="syncToday">同步今天</el-button>
        <el-button size="small" type="warning" icon="el-icon-refresh" :loading="backfillLoading" @click="backfillSelectedDate">补发所选日期</el-button>
      </div>
    </div>

    <el-row :gutter="10" class="summary-row">
      <el-col :span="6">
        <div class="summary-card">
          <div class="summary-label">组织数</div>
          <div class="summary-value">{{ formatNumber(summary.organizationCount) }}</div>
        </div>
      </el-col>
      <el-col :span="6">
        <div class="summary-card">
          <div class="summary-label">官方活跃账号</div>
          <div class="summary-value blue">{{ formatNumber(summary.officialActiveAccountCount) }}</div>
        </div>
      </el-col>
      <el-col :span="6">
        <div class="summary-card">
          <div class="summary-label">报表使用中</div>
          <div class="summary-value green">{{ formatNumber(summary.enabledAccountCount) }}</div>
        </div>
      </el-col>
      <el-col :span="6">
        <div class="summary-card">
          <div class="summary-label">不再使用</div>
          <div class="summary-value orange">{{ formatNumber(summary.disabledAccountCount) }}</div>
        </div>
      </el-col>
    </el-row>

    <div class="filter-bar">
      <el-date-picker
          v-model="filters.date"
          size="small"
          type="date"
          value-format="yyyy-MM-dd"
          placeholder="数据日期"
          style="width: 150px"
          @change="search"
      />
      <el-input
          v-model="filters.keyword"
          size="small"
          clearable
          placeholder="账号ID、账号名、组织名"
          style="width: 230px"
          @keyup.enter.native="search"
      />
      <el-select v-model="filters.organizationId" size="small" filterable clearable placeholder="组织" style="width: 240px">
        <el-option
            v-for="item in organizations"
            :key="item.organization_id"
            :label="orgLabel(item)"
            :value="item.organization_id"
        />
      </el-select>
      <el-select v-model="filters.inUse" size="small" placeholder="使用状态" style="width: 120px">
        <el-option label="全部" value="" />
        <el-option label="使用中" value="1" />
        <el-option label="不再使用" value="0" />
      </el-select>
      <el-select v-model="filters.officialActive" size="small" placeholder="官方状态" style="width: 130px">
        <el-option label="全部" value="" />
        <el-option label="活跃" value="1" />
        <el-option label="非活跃" value="0" />
      </el-select>
      <el-button size="small" type="primary" icon="el-icon-search" @click="search">查询</el-button>
      <el-button size="small" icon="el-icon-refresh-left" @click="reset">重置</el-button>
      <el-button size="small" :disabled="selectedRows.length === 0" @click="batchEnable(true)">批量启用</el-button>
      <el-button size="small" :disabled="selectedRows.length === 0" @click="batchEnable(false)">批量停用</el-button>
    </div>

    <el-table
        ref="table"
        v-loading="loading"
        :data="rows"
        border
        size="small"
        height="calc(100vh - 310px)"
        @selection-change="selectedRows = $event"
    >
      <el-table-column type="selection" width="42" fixed />
      <el-table-column prop="organization_names" label="所属组织" min-width="240" show-overflow-tooltip />
      <el-table-column prop="snapshot_date" label="数据日期" width="110" />
      <el-table-column prop="account_id" label="账号ID" width="150" />
      <el-table-column prop="account_name" label="账号名称" min-width="220" show-overflow-tooltip />
      <el-table-column label="旧版消耗" width="110" align="right">
        <template slot-scope="{ row }">{{ formatAmount(row.old_cost) }}</template>
      </el-table-column>
      <el-table-column label="智擎消耗" width="110" align="right">
        <template slot-scope="{ row }">{{ formatAmount(row.std_cost) }}</template>
      </el-table-column>
      <el-table-column label="总消耗" width="110" align="right">
        <template slot-scope="{ row }">{{ formatAmount(row.total_cost) }}</template>
      </el-table-column>
      <el-table-column label="官方状态" width="100">
        <template slot-scope="{ row }">
          <el-tag v-if="Number(row.official_active) === 1" size="mini" type="success">活跃</el-tag>
          <el-tag v-else size="mini" type="info">非活跃</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="报表使用" width="150">
        <template slot-scope="{ row }">
          <el-switch
              :value="Number(row.use_status) === 1"
              active-text="使用"
              inactive-text="停用"
              @change="changeUsage(row, $event)"
          />
        </template>
      </el-table-column>
      <el-table-column prop="disabled_reason" label="停用原因" min-width="180" show-overflow-tooltip />
      <el-table-column
          prop="last_seen_at"
          label="最近同步"
          width="190"
          class-name="time-column"
          :formatter="formatBeijingTimeCell"
      />
      <el-table-column label="操作" width="120" fixed="right">
        <template slot-scope="{ row }">
          <el-button type="text" size="small" @click="changeUsage(row, Number(row.use_status) !== 1)">
            {{ Number(row.use_status) === 1 ? '停用' : '启用' }}
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="pager">
      <span>共 {{ total }} 条</span>
      <el-pagination
          background
          layout="sizes, prev, pager, next, jumper"
          :current-page="page"
          :page-size="pageSize"
          :page-sizes="[30, 50, 100, 200]"
          :total="total"
          @current-change="handlePageChange"
          @size-change="handleSizeChange"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: 'OceanEngineAccountDirectory',
  data() {
    return {
      loading: false,
      syncLoading: false,
      backfillLoading: false,
      rows: [],
      selectedRows: [],
      organizations: [],
      summary: {},
      total: 0,
      page: 1,
      pageSize: 50,
      filters: {
        date: this.todayDate(),
        keyword: '',
        organizationId: '',
        inUse: '',
        officialActive: '1'
      }
    }
  },
  mounted() {
    this.loadSummary()
    this.loadOrganizations()
    this.loadAccounts()
  },
  methods: {
    async loadSummary() {
      const response = await this.$axios.get('/oceanengine/accounts/summary', {
        params: {
          date: this.filters.date
        }
      })
      if (response.data && response.data.success) {
        this.summary = response.data.data || {}
      }
    },
    async loadOrganizations() {
      const response = await this.$axios.get('/oceanengine/accounts/organizations')
      if (response.data && response.data.success) {
        this.organizations = (response.data.data && response.data.data.list) || []
      }
    },
    async loadAccounts() {
      this.loading = true
      try {
        const response = await this.$axios.get('/oceanengine/accounts', {
          params: Object.assign({}, this.filters, {
            page: this.page,
            pageSize: this.pageSize
          })
        })
        if (!response.data || !response.data.success) {
          this.$message.error((response.data && response.data.msg) || '查询失败')
          return
        }
        const data = response.data.data || {}
        this.rows = data.list || []
        this.total = Number(data.total || 0)
      } finally {
        this.loading = false
      }
    },
    async syncToday() {
      this.syncLoading = true
      try {
        const today = this.todayDate()
        const response = await this.$axios.post('/oceanengine/accounts/sync', {
          date: today
        })
        if (!response.data || !response.data.success) {
          this.$message.error((response.data && response.data.msg) || '同步失败')
          return
        }
        this.filters.date = today
        this.$message.success(this.syncResultMessage('同步今天完成', response.data.data))
        await this.loadSummary()
        await this.loadOrganizations()
        await this.loadAccounts()
      } finally {
        this.syncLoading = false
      }
    },
    async backfillSelectedDate() {
      const date = this.filters.date || this.todayDate()
      try {
        await this.$confirm('确认重新拉取 ' + date + ' 的巨量账号快照？', '补发所选日期', {
          type: 'warning',
          confirmButtonText: '确认补发',
          cancelButtonText: '取消'
        })
      } catch (e) {
        return
      }
      this.backfillLoading = true
      try {
        const response = await this.$axios.post('/oceanengine/accounts/sync', {
          date
        })
        if (!response.data || !response.data.success) {
          this.$message.error((response.data && response.data.msg) || '补发失败')
          return
        }
        this.$message.success(this.syncResultMessage('补发完成', response.data.data))
        await this.loadSummary()
        await this.loadOrganizations()
        await this.loadAccounts()
      } finally {
        this.backfillLoading = false
      }
    },
    syncResultMessage(prefix, data) {
      const result = data || {}
      const parts = []
      if (result.snapshotDate) {
        parts.push('日期 ' + result.snapshotDate)
      }
      if (result.accountCount !== undefined) {
        parts.push('账号 ' + this.formatNumber(result.accountCount))
      }
      if (result.oldAccountCount !== undefined) {
        parts.push('旧版 ' + this.formatNumber(result.oldAccountCount))
      }
      if (result.stdAccountCount !== undefined) {
        parts.push('智擎 ' + this.formatNumber(result.stdAccountCount))
      }
      return parts.length ? prefix + '：' + parts.join('，') : prefix
    },
    search() {
      this.page = 1
      this.loadAccounts()
      this.loadSummary()
    },
    reset() {
      this.filters = {
        date: this.todayDate(),
        keyword: '',
        organizationId: '',
        inUse: '',
        officialActive: '1'
      }
      this.search()
    },
    async changeUsage(row, inUse) {
      let reason = ''
      if (!inUse) {
        try {
          const result = await this.$prompt('请输入停用原因', '停用账号', {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            inputPlaceholder: '可为空'
          })
          reason = result.value || ''
        } catch (e) {
          return
        }
      }
      const response = await this.$axios.post('/oceanengine/accounts/' + row.account_id + '/usage', {
        inUse,
        reason
      })
      if (!response.data || !response.data.success) {
        this.$message.error((response.data && response.data.msg) || '更新失败')
        return
      }
      this.$message.success('已更新')
      await this.loadSummary()
      await this.loadAccounts()
    },
    async batchEnable(inUse) {
      const accountIds = this.selectedRows.map(item => item.account_id)
      if (accountIds.length === 0) {
        return
      }
      let reason = ''
      if (!inUse) {
        try {
          const result = await this.$prompt('请输入批量停用原因', '批量停用账号', {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            inputPlaceholder: '可为空'
          })
          reason = result.value || ''
        } catch (e) {
          return
        }
      } else {
        await this.$confirm('确认批量启用选中的账号？', '批量启用', { type: 'warning' })
      }
      const response = await this.$axios.post('/oceanengine/accounts/usage/batch', {
        accountIds,
        inUse,
        reason
      })
      if (!response.data || !response.data.success) {
        this.$message.error((response.data && response.data.msg) || '批量更新失败')
        return
      }
      this.$message.success('已更新 ' + response.data.data.affected + ' 个账号')
      this.selectedRows = []
      await this.loadSummary()
      await this.loadAccounts()
    },
    handlePageChange(page) {
      this.page = page
      this.loadAccounts()
    },
    handleSizeChange(size) {
      this.pageSize = size
      this.page = 1
      this.loadAccounts()
    },
    orgLabel(item) {
      const prefix = item.depth ? Array(Number(item.depth) + 1).join('　') : ''
      return prefix + (item.organization_name || item.organization_id) + '（' + (item.enabled_accounts || 0) + '）'
    },
    formatNumber(value) {
      const number = Number(value || 0)
      return number.toLocaleString()
    },
    formatAmount(value) {
      const number = Number(value || 0)
      if (!Number.isFinite(number)) {
        return '-'
      }
      return number.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },
    todayDate() {
      const parts = new Intl.DateTimeFormat('zh-CN', {
        timeZone: 'Asia/Shanghai',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).formatToParts(new Date()).reduce((result, part) => {
        if (part.type !== 'literal') {
          result[part.type] = part.value
        }
        return result
      }, {})
      return `${parts.year}-${parts.month}-${parts.day}`
    },
    formatBeijingTimeCell(row, column, value) {
      return this.formatBeijingTime(value)
    },
    formatBeijingTime(value) {
      if (!value) {
        return '-'
      }
      const text = String(value).trim()
      let date
      if (/Z$|[+-]\d{2}:?\d{2}$/.test(text)) {
        date = new Date(text)
      } else {
        const normalized = text.replace('T', ' ').replace(/\.\d+$/, '')
        date = new Date(normalized.replace(/-/g, '/'))
      }
      if (!date || Number.isNaN(date.getTime())) {
        return text.replace('T', ' ').replace(/\.\d+([+-]\d{2}:?\d{2}|Z)?$/, '')
      }
      const parts = new Intl.DateTimeFormat('zh-CN', {
        timeZone: 'Asia/Shanghai',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }).formatToParts(date).reduce((result, part) => {
        if (part.type !== 'literal') {
          result[part.type] = part.value
        }
        return result
      }, {})
      return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`
    }
  }
}
</script>

<style scoped>
.ocean-account-page {
  padding: 18px;
  min-height: 100%;
  background: #f5f7fb;
  box-sizing: border-box;
}

.page-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.page-title h2 {
  margin: 0;
  font-size: 20px;
  color: #1f2937;
}

.page-title p {
  margin: 6px 0 0;
  font-size: 13px;
  color: #7b8794;
}

.title-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.summary-row {
  margin-bottom: 10px;
}

.summary-card {
  height: 72px;
  padding: 12px 14px;
  background: #fff;
  border: 1px solid #e5eaf2;
  border-radius: 6px;
  box-sizing: border-box;
}

.summary-label {
  font-size: 12px;
  color: #7b8794;
}

.summary-value {
  margin-top: 8px;
  font-size: 22px;
  font-weight: 700;
  color: #1f2937;
}

.summary-value.blue {
  color: #409eff;
}

.summary-value.green {
  color: #22a06b;
}

.summary-value.orange {
  color: #e68a00;
}

.filter-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px;
  margin-bottom: 10px;
  background: #fff;
  border: 1px solid #e5eaf2;
  border-radius: 6px;
}

.pager {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 14px;
  padding: 10px 0;
  color: #606266;
}

.ocean-account-page ::v-deep .time-column .cell {
  white-space: nowrap;
}
</style>
